import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice, ProductType, SalesAgreementPriceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { Button, format, ICollapseProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { applyWholeNumberCurrencyFormat } from '../utilities/currency-format.util';
import { getExtensionPropertyNumber, getExtensionPropertyBoolean } from '../utilities/extension-property.util';

const defaultPrice: number = 0;
const freePrice: number = 0;

const GHA_REFERENCE_PRICE_KEY = 'GHA_ReferencePrice';
const GHA_ISOUTLET_KEY = 'GHA_IsOutlet';

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
    salesAgreementPricePrompt?: string;
    salesAgreementExpirationDatePrompt?: string;
    salesAgreementCommittedQuantityPrompt?: string;
    salesAgreementRemainingQuantityPrompt?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    product?: SimpleProduct;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    referencePriceText?: string;
    referencePricePopupText?: string;
    previousPriceText?: string;
    previousPriceInfoText?: string;
    isPriceMinMaxEnabled?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    salesAgreementLineId?: number;
    priceResources?: IPriceComponentResources;
    salesAgreementDetailCollapseProps?: ICollapseProps;
    currencyCode?: string;
    hideInfoTooltip?: boolean;
    hideReferencePrice?: boolean;
    salesAgreementDetailOnClick?(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
}

/**
 * The interface of price component.
 */
export interface IPriceComponent extends IComponent<IPriceComponentProps> {}

const priceComponentActions = {};

/**
 * Get the original price from price component props.
 * @param props - The IPriceComponentProps.
 * @returns The original price.
 */
const getOriginalPrice = (props: IPriceComponentProps): number => {
    // There are two different scenarios:
    // TradeAgreement is populated: the adjustedprice contains the actual original price
    // TradeAgreement is not populated: the adjustedprice contains the active price and baseprice contains the original price
    if (props.data.price.TradeAgreementPrice) {
        return props.data.price.AdjustedPrice ?? props.data.price.TradeAgreementPrice ?? props.data.price.BasePrice ?? defaultPrice;
    } else {
        return props.data.price.BasePrice ?? defaultPrice;
    }
};

const getActivePrice = (props: IPriceComponentProps): number => {
    if (showPriceRangeForMasterProduct(props) && props.data.price.MinVariantPrice !== undefined) {
        return props.data.price.MinVariantPrice;
    }

    if (props.data.price.CustomerContextualPrice !== undefined) {
        return props.data.price.CustomerContextualPrice;
    }

    return getOriginalPrice(props);
};

/**
 * Get the formatted currency.
 * @param props - The props.
 * @param price - The price.
 * @param shouldUseFreePriceText - The shouldUseFreePriceText.
 * @returns The formatted currency.
 */
const formatCurrency = (
    props: IPriceComponentProps,
    price: number | undefined,
    shouldUseFreePriceText: boolean | undefined,
    currencyCode?: string | undefined
): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price, currencyCode);
};

const showPriceRangeForMasterProduct = (props: IPriceComponentProps): boolean => {
    return (
        (props.isPriceMinMaxEnabled ?? false) &&
        (props.product?.ProductTypeValue === ProductType.Master || props.product?.ProductTypeValue === ProductType.KitMaster) &&
        props.data.price.MinVariantPrice !== undefined &&
        props.data.price.MaxVariantPrice !== undefined
    );
};

/**
 * Get the reference price from extension properties
 * @param props - The IPriceComponentProps
 * @returns The reference price or null if not found
 */
const getReferencePrice = (props: IPriceComponentProps): number | null => {
    const referencePrice = getExtensionPropertyNumber(props.data.price.ExtensionProperties, GHA_REFERENCE_PRICE_KEY);

    return referencePrice > 0 ? referencePrice : null;
};

const isOutlet = (props: IPriceComponentProps): boolean => {
    const isOutlet = getExtensionPropertyBoolean(props.product?.ExtensionProperties, GHA_ISOUTLET_KEY);
    return isOutlet;
};

/**
 * Determines if current price should be displayed in red (discount styling)
 * @param props - The IPriceComponentProps
 * @returns True if current price should be red
 */
const shouldCurrentPriceBeRed = (props: IPriceComponentProps): boolean => {
    const currentPrice = getActivePrice(props);
    const originalPrice = getOriginalPrice(props);
    const referencePrice = getReferencePrice(props);

    // Current price is red when:
    // 1. Current < Original AND Reference is null/undefined
    // 2. Current < Original AND Reference > Current (reference price is higher than current)
    if (currentPrice < originalPrice) {
        if (!referencePrice) {
            return true; // Scenario 1: Only current and original prices
        }
        if (referencePrice > currentPrice) {
            return true; // Scenario 2: Reference price is higher than current
        }
        // Scenario 3: Reference price is lower than current → current price is black
        return false;
    }

    return false;
};

/**
 * Determines the appropriate CSS class for current pricing
 * @param props - The IPriceComponentProps
 * @returns CSS class suffix for pricing
 */
const getPriceClass = (props: IPriceComponentProps): string => {
    if (isOutlet(props)) {
        return 'outlet';
    }
    if (shouldCurrentPriceBeRed(props)) {
        return 'discount';
    }
    return 'regular';
};

/**
 * Render current price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false, props.currencyCode);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false, props.currencyCode);
    const priceClassSuffix = getPriceClass(props);

    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        return (
            <span className={`gh-price__pricerange gh-price__pricerange--${priceClassSuffix}`} itemProp='price'>
                <span className='gh-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='gh-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='gh-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }

    return (
        <span className={`gh-price__actual gh-price__actual--${priceClassSuffix}`} itemProp='price'>
            {formatCurrency(props, getActivePrice(props), true, props.currencyCode)}
        </span>
    );
};

/**
 * Render original price based on pricing scenario
 * @param props - The props
 * @returns The JSX.Element or null
 */
const renderOriginalPrice = (props: IPriceComponentProps): JSX.Element | null => {
    const isOutletProduct = isOutlet(props);
    const currentPrice = getActivePrice(props);
    const originalPrice = getOriginalPrice(props);
    const referencePrice = getReferencePrice(props);

    // Only show original price if current price is lower
    if (currentPrice >= originalPrice) {
        return null;
    }

    const formattedOriginalPrice = formatCurrency(props, originalPrice, true, props.currencyCode);

    // Scenario 1: Current < Original, but no reference price OR reference price > original
    // Show original price in parentheses without label
    if ((!referencePrice || referencePrice >= originalPrice) && !isOutletProduct) {
        return <span className='gh-price__original gh-price__original--simple'>({formattedOriginalPrice})</span>;
    }

    const tooltipText = props.previousPriceInfoText || '';

    // Scenario 2: Current < Original, and reference price <= original
    // Show original price with "Ord pris:" label
    let originalPriceLabel = props.originalPriceText || 'Ord pris:';
    if (isOutletProduct) {
        originalPriceLabel = props.previousPriceText || 'Previous price:';
    }

    return (
        <div className='gh-price__original--labeled'>
            <span className='gh-price__original-label'>{originalPriceLabel}</span>
            <span className='gh-price__original-price'>{formattedOriginalPrice}</span>
            {isOutletProduct && !props.hideInfoTooltip && getInfoPopup(tooltipText)}
        </div>
    );
};

/**
 * Render reference price display
 * @param props - The props
 * @returns The JSX.Element or null
 */
const renderReferencePrice = (props: IPriceComponentProps): JSX.Element | null => {
    if (props.hideReferencePrice) {
        return null;
    }

    const isOutletProduct = isOutlet(props);
    if (isOutletProduct) {
        return null;
    }

    const currentPrice = getActivePrice(props);
    const originalPrice = getOriginalPrice(props);
    const referencePrice = getReferencePrice(props);

    // Only show reference price when current price is lower than original price
    // AND reference price exists
    if (!referencePrice || originalPrice === currentPrice || referencePrice >= originalPrice) {
        return null;
    }

    const formattedReferencePrice = formatCurrency(props, referencePrice, false, props.currencyCode);
    const referencePriceLabel = props.referencePriceText;
    const tooltipText = props.referencePricePopupText || '';

    return (
        <div className='gh-price__reference-simple'>
            <span className='gh-price__reference-label'>{referencePriceLabel}</span>
            <span className='gh-price__reference-price'>{formattedReferencePrice}</span>
            {!props.hideInfoTooltip && tooltipText && getInfoPopup(tooltipText)}
        </div>
    );
};

const getInfoPopup = (popupText: string): JSX.Element => {
    return (
        <button type='button' className='gh-price__info-tooltip' aria-label='Price information'>
            ℹ
            <div
                className='gh-price__tooltip-content'
                dangerouslySetInnerHTML={{ __html: popupText.replace(/(\r\n|\r|\n|\\n)/g, '<br />') }}
            />
        </button>
    );
};

const renderSalesAgreementDetail = (props: IPriceComponentProps): JSX.Element => {
    // In most cases there should be only one sales agreement line.
    // If there are more than one, only the first one will be shown.
    const firstPriceLine: SalesAgreementPriceLine = (props.data.price.AttainablePriceLines ?? [])[0] as SalesAgreementPriceLine;
    return (
        <p className={classnames('gh-price__salesagreementdetail', props.className)}>
            <p>
                {/* Available date */}
                {` · ${format(
                    props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                    props.context.cultureFormatter.formatDate(firstPriceLine.EffectiveDate as Date),
                    props.context.cultureFormatter.formatDate(firstPriceLine.ExpirationDate as Date)
                )}`}
            </p>
            <p>
                {/* Quantity commitment */}
                {` · ${format(props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '', firstPriceLine.CommittedQuantity)}`}
            </p>
            <p>
                {/* Remaining quantity to qualify */}
                {` · ${format(props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '', firstPriceLine.RemainingQuantity)}`}
            </p>
        </p>
    );
};

/**
 * Price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps): JSX.Element | null => {
    applyWholeNumberCurrencyFormat(props.context);
    // CustomerContextualPrice could be zero
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    // Determine if contract price is avaiable/applied by:
    //   isFeatureEnabled and SA price != 0, for PDP.
    //   SA line id != 0, for cart line.
    if (props.isSalesAgreementPriceFeatureEnabled && props.data.price.SalesAgreementPrice !== 0) {
        const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
        const toggle = () => {
            setIsExpanded(!isExpanded);
        };
        return (
            <span className={classnames('gh-price', props.className)}>
                <span className={classnames('gh-price__salesagreementprice', props.className)}>
                    <p className='gh-price__actual' itemProp='price'>
                        {formatCurrency(props, props.data.price.SalesAgreementPrice ?? props.data.price.CustomerContextualPrice, true)}
                    </p>
                    <Button
                        className={isExpanded ? 'gh-price__salesagreementprompt_expanded' : 'gh-price__salesagreementprompt_collapsed'}
                        aria-label={props.priceResources?.salesAgreementPricePrompt}
                        onClick={toggle}
                        aria-expanded={isExpanded}
                        tabIndex={0}
                    >
                        {props.priceResources?.salesAgreementPricePrompt}
                    </Button>
                    {isExpanded && renderSalesAgreementDetail(props)}
                </span>
            </span>
        );
    }

    const priceClassSuffix = getPriceClass(props);

    return (
        <span className={classnames('gh-price', `gh-price--${priceClassSuffix}`, props.className)}>
            {renderCurrentPrice(props)}
            {renderReferencePrice(props)}
            {renderOriginalPrice(props)}
        </span>
    );
};

/**
 * The PriceComponent.
 */
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponent<IPriceComponent>('Price', {
    component: Price,
    ...priceComponentActions
});
